import { fg } from '@atlaskit/platform-feature-flags/fg';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';
import {
	rewriteBaseToCustomDomain,
	type CustomDomainProduct,
} from '@atlassian/conversation-assistant-utils/rewriteBaseToCustomDomain';

import { type CloudIdResponse, type CloudIdVariables } from './types.ts';

const CloudIdLookupQuery = `
query tenantQuery($cloudIds: [ID!]) {
  tenantContexts(cloudIds: $cloudIds) {
    cloudUrl
    orgId
    customDomains {
      product
      hostName
    }
  }
}
`;

/**
 * The set of products that support per-product custom-domain URL rewriting.
 *
 * Alias of `CustomDomainProduct` so product support stays coupled to
 * `rewriteBaseToCustomDomain` without re-exporting imported symbols from this package.
 */
export type CloudUrlProduct = CustomDomainProduct;

type CloudUrlResult = {
	cloudUrl: string;
	orgId: string;
};

type RawTenantContext = CloudIdResponse['tenantContexts'][0];

// Cached per `cloudId` — the raw AGG payload is product-agnostic, so we fetch
// once per tenant and let the rewrite step branch on product. The cache stores
// the in-flight Promise so concurrent product lookups dedupe to one request.
const rawTenantCache = new Map<string, Promise<RawTenantContext | undefined>>();

const fetchTenantContext = async (cloudId: string): Promise<RawTenantContext | undefined> => {
	const response = await performGqlFetch<CloudIdResponse, CloudIdVariables>({
		gqlQuery: CloudIdLookupQuery,
		variables: { cloudIds: [cloudId] },
	});
	return response.data?.tenantContexts?.[0];
};

/**
 * Fetches the cloud URL and org ID for a given site.
 *
 * When `product` is provided, the returned `cloudUrl` hostname is rewritten to
 * the tenant's per-product custom domain (if one is configured). Without `product`,
 * the canonical `*.atlassian.net` hostname is returned unchanged.
 *
 * @throws Will reject if the underlying GQL request fails. Callers should wrap in try/catch.
 */
export const getCloudUrlFromId = async (
	cloudId: string,
	product?: CloudUrlProduct,
): Promise<CloudUrlResult | undefined> => {
	const cachingEnabled = fg('aifc_create_enabled');
	let inFlight = cachingEnabled ? rawTenantCache.get(cloudId) : undefined;

	if (!inFlight) {
		inFlight = fetchTenantContext(cloudId);
		if (cachingEnabled) {
			rawTenantCache.set(cloudId, inFlight);
			inFlight.then(
				(result) => {
					if (result === undefined) {
						rawTenantCache.delete(cloudId);
					}
				},
				() => {
					rawTenantCache.delete(cloudId);
				},
			);
		}
	}

	const raw = await inFlight;
	if (!raw) {
		return undefined;
	}

	return {
		cloudUrl: product
			? rewriteBaseToCustomDomain(raw.cloudUrl, product, raw.customDomains)
			: raw.cloudUrl,
		orgId: raw.orgId,
	};
};
